import { UploadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import DataTable from '@/components/DataTable';
import ColumnCustomizer from '@/components/DataTable/ColumnCustomizer';
import Sorter from '@/components/DataTable/Sorter';
import DataFilter from '@/components/Filter/DataFilter';
import SearchInput from '@/components/Inputs/SearchInput';
import StyledPagination from '@/components/Pagination/Pagination';
import { PageHeaderTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import { COMPANY_INITIAL_SORT_STATUS } from '@/configs/filter';
import {
  useCompanyHeader,
  useCompanyTableData,
} from '@/data-table-configs/companies';
import CreateCompanyModal from '@/features/Account/components/CreateCompanyModal';
import useCompanyStore, { COMPANY_PAGE_SIZE } from '@/features/Account/store';
import PageHeaderStyle from '@/features/Commons/PageLayout/PageHeader';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useColor from '@/hooks/domain/useColor';
import { useStorageFilterQuery } from '@/hooks/domain/useFilterQuery';
import useRedirect from '@/hooks/domain/useRedirect';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useModalStore from '@/stores/pure/ModalStore';
import { ACCOUNT_COLUMNS } from '@/types/filterKeyConst';
import filterUtils from '@/utils/filter';
import { filterMap } from '@/utils/lazy';
import { buildQuery, highlight, removeHighlight } from '@/utils/util';

const TABLE_KEY = 'company';

const CompanyPage = () => {
  const color = useColor();
  const { isEditor } = useUserRole();
  const appInit = useAppInit();
  const wr = useWorkspaceRouter();
  const [paginatedData, setPaginatedData] = useState<PaginatedData<CompanyT>[]>(
    [],
  );

  useRedirect();

  const {
    createModalStatus,
    listLoading,
    selectedCompany,
    page,
    setCreateModalStatus,
    fetchCompanyList,
  } = useCompanyStore((state) => ({
    createModalStatus: state.createModalStatus,
    page: state.page,
    listLoading: state.listLoading,
    selectedCompany: state.selectedCompany,
    setCreateModalStatus: state.setCreateModalStatus,
    fetchCompanyList: state.fetchCompanyList,
  }));

  const setModalStatus = useModalStore((state) => state.setModalStatus);
  const { toggleColumnVisible, changeColumnOrder, changeColumnWidth } =
    useColumnConfigStore(
      ({ toggleColumnVisible, changeColumnOrder, changeColumnWidth }) => ({
        toggleColumnVisible,
        changeColumnOrder,
        changeColumnWidth,
      }),
    );

  const [sortStatus, setSortStatus] = useState<NotNullableSortStatusT>(
    COMPANY_INITIAL_SORT_STATUS,
  );
  const { currFilterSetQuery, onFilterChange, clearFilter } =
    useStorageFilterQuery(TABLE_KEY);

  type States = {
    searchValue: string;
    searchInput: string;
  };

  const { state, setValue, setState } = useStates<States>({
    searchValue: '',
    searchInput: '',
  });

  const clearSearch = () => {
    setState({
      searchInput: '',
      searchValue: '',
    });
  };

  const fetchList = useCallback(
    async (prevData?: PaginatedData<CompanyT>[], page?: number) => {
      const data = await fetchCompanyList({
        page,
        searchValue: state.searchValue,
        prevData: page ? prevData : [],
        sortStatus: sortStatus,
        fiterSetQuery: currFilterSetQuery,
      });
      setPaginatedData(data);
      if (state.searchValue) {
        highlight(state.searchValue);
      } else {
        removeHighlight();
      }
    },
    [
      JSON.stringify(currFilterSetQuery),
      fetchCompanyList,
      JSON.stringify(sortStatus),
      state.searchValue,
    ],
  );

  useEffect(() => {
    if (appInit) {
      fetchList();
    }
  }, [appInit, fetchList]);

  const filterQuery = useMemo(() => {
    const query = filterUtils.getFilterStatusQuery(currFilterSetQuery);
    if (state.searchValue.trim() !== '') {
      query.push({
        key: ACCOUNT_COLUMNS.NAME_SEARCH,
        value: state.searchValue,
      });
    }
    return buildQuery(query);
  }, [currFilterSetQuery, state.searchValue]);

  const { data: totalCount } = useQuery({
    queryKey: ['company-list-count', filterQuery],
    queryFn: () => service.account.fetchCompanyCount(filterQuery),
    enabled: isEditor,
  });

  const companyHeader = useCompanyHeader();
  const tableData = useCompanyTableData(paginatedData);

  return (
    <Style d="column">
      <PageHeaderStyle>
        <Flex g={12} a="center">
          <PageHeaderTitle>Accounts</PageHeaderTitle>
          <Typo type="label1" color={color.gray600}>
            {totalCount || 0}
          </Typo>
          <SearchInput
            placeholder="Search"
            value={state.searchInput}
            onChange={(e) => setValue('searchInput', e.currentTarget.value)}
            onBlur={() => setValue('searchValue', state.searchInput)}
            onClickClear={clearSearch}
          />
        </Flex>

        {isEditor && (
          <Flex a="center" g="6px">
            <Button
              leftIcon={<UploadOutlined />}
              variant="secondary"
              sizeType="m"
              onClick={() => {
                wr.push('/companies/upload');
                setModalStatus('companyUploadModal', true);
              }}
            >
              Upload
            </Button>
            <Button
              leftIcon={<Plus />}
              sizeType="m"
              onClick={() => setCreateModalStatus(true)}
            >
              Account
            </Button>
          </Flex>
        )}
      </PageHeaderStyle>
      <Flex j="space-between" padding="0px 32px">
        <DataFilter
          tableName={TABLE_KEY}
          filterOptions={companyHeader}
          currFilterSetQuery={currFilterSetQuery}
          onFilterChange={onFilterChange}
          resetFilter={clearFilter}
        />
        <Flex>
          <Sorter
            options={filterMap(companyHeader, (col) => {
              if (!col.sortKey || !col.orderable) return null;
              return {
                sortKey: col.sortKey,
                sortName: col.columnName,
                orderType: col.orderType || 'default',
              };
            })}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
          <ColumnCustomizer
            columns={companyHeader}
            onCustomizeColumnSelect={(key) => {
              toggleColumnVisible('account_view', key);
            }}
          />
        </Flex>
      </Flex>
      <DataTable
        name={TABLE_KEY}
        style={{
          height: 'auto',
          maxHeight: 'calc(100vh - 160px)',
        }}
        id="company-data-table"
        columns={companyHeader}
        datas={tableData}
        selectedRowKey={selectedCompany}
        sortStatus={sortStatus}
        loading={listLoading}
        onSortChange={setSortStatus}
        asyncData
        customizable
        columnChangeOrderHandler={(key, order) =>
          changeColumnOrder('account_view', key, order)
        }
        columnResizeHandler={(key, width) =>
          changeColumnWidth('account_view', key, width)
        }
        onCustomizeColumnSelect={(key) =>
          toggleColumnVisible('account_view', key)
        }
        openButtonOffsetX={220}
      />
      <PaginationWrapper>
        <StyledPagination
          loading={listLoading}
          pageSize={COMPANY_PAGE_SIZE}
          currentLength={tableData.length}
          total={totalCount || 0}
          current={page}
          onChange={(page) => {
            fetchList(paginatedData, page);
          }}
        />
      </PaginationWrapper>
      {createModalStatus && <CreateCompanyModal />}
    </Style>
  );
};

const Style = styled(Flex)`
  width: 100%;
  height: 100vh;
  position: relative;
  .company-list-filter-sort {
    width: 100%;
    padding: 0 8px;
    z-index: 10;
    background-color: ${(props) => props.theme.colors.gray100};
  }
`;

const PaginationWrapper = styled(Flex).attrs({
  a: 'center',
  j: 'flex-start',
})`
  border-top: 1px solid ${(props) => props.theme.colors.ND04};
  width: 100%;
  height: 44px;
  padding: 0 32px;
`;

export default CompanyPage;
